import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { adminApi, mediaRaw } from "./adminApi";
import { StatusBadge, Field, LinesEditor, MediaPicker } from "./components";
import { DynIcon, ICON_NAMES } from "@/lib/icons";

const EMPTY = { name: "", description: "", icon: "Building2", cover_image_url: "", challenges: [], benefits: [], solution_ids: [], case_studies: [], videos: [], pdfs: [], order: 0, status: "draft" };

export default function IndustriesAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-industries"], queryFn: adminApi.listIndustries });
  const [editing, setEditing] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-industries"] });
  const list = data || [];

  const move = async (idx, dir) => {
    const arr = [...list]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await adminApi.reorderIndustries(arr.map((s) => s.id)); refresh();
  };
  const toggle = async (s) => { await adminApi.industryStatus(s.id, s.status === "published" ? "draft" : "published"); refresh(); };
  const remove = async (s) => { if (window.confirm(`Delete "${s.name}"?`)) { await adminApi.deleteIndustry(s.id); refresh(); } };

  return (
    <div data-testid="admin-industries-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-head text-3xl font-extrabold text-white">Industries</h1><p className="mt-1 text-slate-400">Manage industry application categories.</p></div>
        <Button data-testid="industry-add-btn" onClick={() => setEditing(EMPTY)} className="cyan-grad text-[#041021] font-bold"><Plus className="h-4 w-4" /> New Industry</Button>
      </div>
      {isLoading ? <Loader2 className="mt-10 h-8 w-8 animate-spin text-cyan-400" /> : (
        <div className="mt-6 space-y-3">
          {list.length === 0 && <p className="rounded-2xl glass p-8 text-center text-slate-500">No industries yet.</p>}
          {list.map((s, i) => (
            <div key={s.id} data-testid={`industry-row-${i}`} className="flex items-center gap-4 rounded-2xl glass p-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-cyan-400/40 text-cyan-300"><DynIcon name={s.icon} className="h-6 w-6" /></div>
              <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><h3 className="truncate font-head font-bold text-white">{s.name}</h3><StatusBadge status={s.status} /></div><p className="truncate text-sm text-slate-400">{s.description}</p></div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => move(i, -1)} className="rounded-lg p-2 text-slate-400 hover:text-cyan-300"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} className="rounded-lg p-2 text-slate-400 hover:text-cyan-300"><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => toggle(s)} data-testid={`industry-publish-${i}`} className="rounded-lg p-2 text-slate-400 hover:text-emerald-300">{s.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                <button onClick={() => setEditing(s)} data-testid={`industry-edit-${i}`} className="rounded-lg p-2 text-slate-400 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(s)} className="rounded-lg p-2 text-slate-400 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <IndustryEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function IndustryEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (publish) => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = { ...form, status: publish ? "published" : form.status };
      if (initial.id) await adminApi.updateIndustry(initial.id, body); else await adminApi.createIndustry(body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-[#0b1730] border-cyan-500/20 text-white" data-testid="industry-editor">
        <DialogHeader><DialogTitle className="font-head">{initial.id ? "Edit" : "New"} Industry</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Name"><Input data-testid="ind-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#050b18] border-slate-700" /></Field>
          <Field label="Description"><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="w-full rounded-lg border border-slate-700 bg-[#050b18] px-3 py-2 text-sm outline-none focus:border-cyan-500" /></Field>
          <Field label="Icon">
            <select value={form.icon} onChange={(e) => set("icon", e.target.value)} className="w-full rounded-lg border border-slate-700 bg-[#050b18] px-3 py-2 text-sm outline-none focus:border-cyan-500">
              {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Cover Image">
            <div className="flex items-center gap-3">
              {form.cover_image_url && <img src={form.cover_image_url} alt="" className="h-16 w-24 rounded-lg object-cover" />}
              <Button type="button" variant="outline" className="border-slate-700" onClick={() => setPicker(true)} data-testid="ind-pick-cover">Choose image</Button>
              {form.cover_image_url && <button onClick={() => set("cover_image_url", "")} className="text-rose-300"><X className="h-4 w-4" /></button>}
            </div>
          </Field>
          <LinesEditor label="Challenges" value={form.challenges} onChange={(v) => set("challenges", v)} />
          <LinesEditor label="Benefits" value={form.benefits} onChange={(v) => set("benefits", v)} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="outline" className="border-slate-700" disabled={saving} onClick={() => save(false)}>Save Draft</Button>
          <Button className="cyan-grad text-[#041021] font-bold" disabled={saving} onClick={() => save(true)} data-testid="ind-publish">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Publish"}</Button>
        </DialogFooter>
        {picker && <MediaPicker open onClose={() => setPicker(false)} kind="image" onSelect={(m) => { set("cover_image_url", mediaRaw(m.id)); setPicker(false); }} />}
      </DialogContent>
    </Dialog>
  );
}
